import { Button as ButtonAnt, Dropdown, Menu } from 'antd'
import { Permission } from 'common/constants'
import { getNs } from 'common/i18n-config'
import { FeatureFlagNames } from 'common/open-features'
import { useCheckFlag } from 'common/open-features/hooks'
import React, { PropsWithChildren } from 'react'

import { If } from '../../components'
import { ifElse, someTrue } from '../../helpers'
import { StyledTooltipOverlay } from '../../shared'
import { Tooltip } from '../tooltip'
import { Button, ButtonPerStyle, MenuIcon, MenuStyle, Wrapper } from './styles'

const { t, useNsReady } = getNs(['common', 'components/organisms/assets/asset-list'])

export const TableDropdownMenu = ({
  data,
  permissions,
  onCopy,
  onDelete,
  onCorrection,
  isEnableCorrectButton,
  isHavePermissionToCorrect,
}: PropsWithChildren<Props>) => {
  useNsReady()

  const { isEnable: isCorrectionBlockedReasonEnabled } = useCheckFlag(FeatureFlagNames.AssetCorrectionBlockedReason)
  const { isEnable: isAssetDeletionReviewEnabled } = useCheckFlag(FeatureFlagNames.AssetDeletionReview)
  const adjusted = data?.adjusted

  const handleCopy = (item: any, event: any) => {
    event.stopPropagation()
    permissions?.isCreate && onCopy?.(item)
  }

  const handleDelete = (item: any, event: any) => {
    event.stopPropagation()
    if (isAssetDeletionReviewEnabled) {
      permissions?.isDelete && onDelete?.(item)
    } else {
      permissions?.isDelete && item.isDeletable && onDelete?.(item)
    }
  }

  const handleCorrection = (biid: string, event: any) => {
    event.stopPropagation()
    onCorrection?.(biid)
  }

  const Actions = (
    <Menu className={MenuStyle} onClick={event => event.domEvent.stopPropagation()}>
      <Menu.Item key="copy">
        {permissions?.isCreate ? (
          <Button
            type="text"
            data-testid={`copy-action-${data.key as string}`}
            id={`copy-action-${data.key as string}`}
            className="text"
            onClick={event => handleCopy(data, event)}
          >
            {t('actions.copy')}
          </Button>
        ) : (
          withPermissionTooltip(
            <ButtonAnt disabled style={ButtonPerStyle} className="btn-no-permission">
              {t('actions.copy')}
            </ButtonAnt>
          )
        )}
      </Menu.Item>
      <Menu.Item key="correction">
        {isHavePermissionToCorrect ? (
          <>
            <If condition={isCorrectionBlockedReasonEnabled}>
              <Button
                type="link"
                className="text"
                disabled={!isEnableCorrectButton}
                onClick={e => handleCorrection(data?.biid, e)}
              >
                {t('actions.correction')}
              </Button>
            </If>
            <If condition={!isCorrectionBlockedReasonEnabled}>
              <Tooltip
                content={ifElse(adjusted, t('tooltip_message.cancel_accelerated_depreciation_to_execute'), '')}
                placement="bottomRight"
              >
                <Button
                  type="link"
                  className="text"
                  disabled={someTrue(!isEnableCorrectButton, adjusted)}
                  onClick={e => handleCorrection(data?.biid, e)}
                >
                  {t('actions.correction')}
                </Button>
              </Tooltip>
            </If>
          </>
        ) : (
          withPermissionTooltip(
            <ButtonAnt type="link" className="btn-no-permission" disabled>
              {t('actions.correction')}
            </ButtonAnt>
          )
        )}
      </Menu.Item>
      <Menu.Item
        key="delete"
        style={isAssetDeletionReviewEnabled ? undefined : { cursor: data.isDeletable ? 'pointer' : 'not-allowed' }}
      >
        {permissions?.isDelete ? (
          <>
            <If condition={isAssetDeletionReviewEnabled}>
              <Button
                danger
                type="text"
                data-testid={`delete-action-${data.key as string}`}
                id={`delete-action-${data.key as string}`}
                className="text"
                onClick={event => handleDelete(data, event)}
              >
                {t('actions.remove')}
              </Button>
            </If>
            <If condition={!isAssetDeletionReviewEnabled}>
              <Tooltip
                content={ifElse(adjusted, t('tooltip_message.cancel_accelerated_depreciation_to_execute'), '')}
                placement="bottomRight"
              >
                <Button
                  danger
                  type="text"
                  data-testid={`delete-action-${data.key as string}`}
                  id={`delete-action-${data.key as string}`}
                  disabled={someTrue(!data.isDeletable, adjusted)}
                  className="text"
                  onClick={event => handleDelete(data, event)}
                >
                  {t('actions.remove')}
                </Button>
              </Tooltip>
            </If>
          </>
        ) : (
          withPermissionTooltip(
            <ButtonAnt disabled style={ButtonPerStyle} className="btn-no-permission">
              {t('actions.remove')}
            </ButtonAnt>
          )
        )}
      </Menu.Item>
    </Menu>
  )

  return (
    <Wrapper>
      <Dropdown overlayClassName="popup-overlay-actions" placement="bottomRight" overlay={Actions} trigger={['click']}>
        <Button
          className="action-button"
          id={`popup-action-${data.key as string}`}
          type="text"
          onClick={event => event.stopPropagation()}
        >
          <MenuIcon />
        </Button>
      </Dropdown>
    </Wrapper>
  )
}

export const withPermissionTooltip = (WrappedComponent: React.ReactNode) => {
  return (
    <Tooltip
      overlayClassName={StyledTooltipOverlay}
      placement="top"
      content={t('actions.no_permission')}
      className="ant-tooltip-custom"
      trigger="hover"
    >
      {WrappedComponent}
    </Tooltip>
  )
}

type Props = {
  data: any
  permissions?: Permission
  onCopy?: (data: any) => void
  onDelete?: (data: any) => void
  onCorrection?: (data: any) => void
  isEnableCorrectButton?: boolean
  isHavePermissionToCorrect?: boolean
}
